import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';
import { PrimaryButton, Sheet, fieldStyle } from '../components/Sheet.js';

/**
 * Connected mailboxes (docs/SCREEN-SPECS.md §12, F-EMAIL-1/5).
 *
 * This screen replaces the family's manual habit, so its job is to make the
 * automation legible: when each mailbox was last read, when a statement last
 * arrived, and what broke if anything did. A poller nobody can see the state of
 * is one nobody can trust.
 */

interface Connection {
  id: string;
  label: string;
  provider: string;
  username: string;
  host: string;
  folder: string;
  status: 'active' | 'error' | 'disabled';
  lastPollAt: string | null;
  lastSeenAt: string | null;
  lastError: string | null;
  daysSinceStatement: number | null;
}

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const minutes = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function Connections() {
  const [adding, setAdding] = useState(false);
  const { data, isPending, error } = useQuery({
    queryKey: ['connections'],
    queryFn: () => api.get<Connection[]>('/connections'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  return (
    <div>
      <h1 className="page-title">Mailboxes</h1>
      <p style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)', margin: '0 0 var(--space-5)' }}>
        Statements are collected automatically. Nothing is ever sent, moved or deleted — the app
        only reads.
      </p>

      <Card title={`${data.length} connected`}>
        {data.length === 0 && (
          <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)', margin: 0 }}>
            No mailboxes yet. Connect the address your CAS statements arrive at and they will be
            picked up on their own.
          </p>
        )}
        {data.map((c) => (
          <Row
            key={c.id}
            left={
              <span>
                <span className="row-title">{c.label}</span>
                <span className="row-meta">
                  {c.username} · checked {ago(c.lastPollAt)}
                  {c.lastSeenAt && ` · last statement ${ago(c.lastSeenAt)}`}
                </span>
                {c.status === 'error' && c.lastError && (
                  // The provider's own words: "authentication failed" and
                  // "connection refused" need different fixes, and paraphrasing
                  // them into "something went wrong" helps nobody.
                  <span style={{ display: 'block', font: 'var(--type-caption)', color: 'var(--color-status-critical)' }}>
                    ⚠ {c.lastError}
                  </span>
                )}
                {/* F-EMAIL-5: a silent mailbox is a failure mode of its own —
                    nothing is broken, and nothing is arriving either. */}
                {c.status === 'active' && c.daysSinceStatement !== null && c.daysSinceStatement > 45 && (
                  <span style={{ display: 'block', font: 'var(--type-caption)', color: 'var(--color-status-warning)' }}>
                    ⚠ no statement for {c.daysSinceStatement} days
                  </span>
                )}
              </span>
            }
            right={<DisconnectButton connection={c} />}
          />
        ))}
      </Card>

      <StatementPasswords />

      <PrimaryButton onClick={() => setAdding(true)} style={{ marginTop: 'var(--space-4)' }}>
        Connect a mailbox
      </PrimaryButton>
      {adding && <ConnectSheet onClose={() => setAdding(false)} />}
    </div>
  );
}

function DisconnectButton({ connection }: { connection: Connection }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.del(`/connections/${connection.id}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        style={{ background: 'none', border: 'none', color: 'var(--color-label-tertiary)', cursor: 'pointer', font: 'var(--type-footnote)', minHeight: 44 }}
      >
        Disconnect
      </button>
    );
  }
  return (
    <button
      onClick={() => remove.mutate()}
      disabled={remove.isPending}
      style={{ background: 'none', border: 'none', color: 'var(--color-status-critical)', cursor: 'pointer', font: 'var(--type-footnote)', minHeight: 44 }}
    >
      {remove.isPending ? 'Removing…' : 'Confirm'}
    </button>
  );
}

const GUIDANCE: Record<string, string> = {
  gmail:
    'Google → Account → Security → 2-Step Verification → App passwords. Your normal Google password will not work here.',
  yahoo: 'Yahoo → Account Security → Generate app password. Your normal Yahoo password will not work here.',
  other: 'Use an app-specific password if your provider offers one.',
};

function ConnectSheet({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    label: '',
    provider: 'gmail',
    username: '',
    appPassword: '',
    host: '',
    folder: 'INBOX',
  });
  const [failure, setFailure] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const connect = useMutation({
    mutationFn: () =>
      api.post('/connections', {
        label: form.label.trim() || form.username.trim(),
        provider: form.provider,
        username: form.username.trim(),
        appPassword: form.appPassword,
        ...(form.provider === 'other' && form.host.trim() ? { host: form.host.trim() } : {}),
        ...(form.folder.trim() && form.folder !== 'INBOX' ? { folder: form.folder.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onClose();
    },
    onError: (err) => {
      const e = err as ApiError;
      setFailure(
        e.status === 401
          ? 'Sign in again before connecting a mailbox — this needs a session less than ten minutes old.'
          : e.message,
      );
    },
  });

  return (
    <Sheet title="Connect a mailbox" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFailure(null);
          connect.mutate();
        }}
      >
        <select style={fieldStyle} value={form.provider} onChange={set('provider')}>
          <option value="gmail">Gmail</option>
          <option value="yahoo">Yahoo</option>
          <option value="other">Other (IMAP)</option>
        </select>
        <input
          style={fieldStyle}
          placeholder="Email address"
          type="email"
          value={form.username}
          onChange={set('username')}
          autoComplete="off"
          required
        />
        {form.provider === 'other' && (
          <input style={fieldStyle} placeholder="IMAP host (e.g. imap.example.com)" value={form.host} onChange={set('host')} required />
        )}
        <input
          style={fieldStyle}
          placeholder="App password"
          type="password"
          value={form.appPassword}
          onChange={set('appPassword')}
          autoComplete="off"
          required
        />
        <input style={fieldStyle} placeholder="Label (optional)" value={form.label} onChange={set('label')} />

        <p className="page-sub">
          {GUIDANCE[form.provider]}
        </p>
        <p style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
          The password is encrypted before it is stored and is never shown again. The connection is
          tested now, so a mistake is caught here rather than silently days later.
        </p>

        {failure && <p style={{ font: 'var(--type-subhead)', color: 'var(--color-status-critical)' }}>{failure}</p>}
        <PrimaryButton type="submit" disabled={connect.isPending || !form.username.trim() || !form.appPassword}>
          {connect.isPending ? 'Testing the connection…' : 'Connect'}
        </PrimaryButton>
      </form>
    </Sheet>
  );
}

interface StoredPassword {
  id: string;
  label: string;
  uses: number;
  lastUsedAt: string | null;
}

/**
 * Passwords for statements that belong to no single person.
 *
 * A CAS requested for the whole family covers several PANs in one PDF, so no
 * member's PAN opens it — the requester chooses a password when asking for the
 * statement. Saving it here is what lets those arrive and unlock without anyone
 * typing anything.
 *
 * The value can never be read back, only replaced. Usage is shown instead,
 * because a password that has never opened anything is probably wrong, and that
 * is worth seeing without revealing it.
 */
function StatementPasswords() {
  const [adding, setAdding] = useState(false);
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['statement-passwords'],
    queryFn: () => api.get<StoredPassword[]>('/statement-passwords'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/statement-passwords/${id}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return (
    <div style={{ marginTop: 'var(--space-6)' }}>
      <Card title="Statement passwords">
        <p style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)', margin: '0 0 var(--space-3)' }}>
          Family statements from CAMS or MF Central cover several PANs in one file, so they use a
          password you chose when requesting them. Save it here and those statements open on their
          own. Statements locked with a member’s PAN need nothing — that is already known.
        </p>

        {(data ?? []).map((p) => (
          <Row
            key={p.id}
            left={
              <span>
                <span style={{ display: 'block', font: 'var(--type-subhead)' }}>{p.label}</span>
                <span style={{ font: 'var(--type-caption)', color: p.uses === 0 ? 'var(--color-status-warning)' : 'var(--color-label-tertiary)' }}>
                  {p.uses === 0
                    ? 'never opened a statement yet'
                    : `opened ${p.uses} statement${p.uses === 1 ? '' : 's'}`}
                </span>
              </span>
            }
            right={
              <button
                onClick={() => remove.mutate(p.id)}
                style={{ background: 'none', border: 'none', color: 'var(--color-label-tertiary)', cursor: 'pointer', font: 'var(--type-footnote)', minHeight: 44 }}
              >
                Remove
              </button>
            }
          />
        ))}

        <button
          onClick={() => setAdding(true)}
          style={{
            width: '100%', minHeight: 44, marginTop: 'var(--space-2)',
            border: '1px solid var(--color-separator)', borderRadius: 'var(--radius-control)',
            background: 'transparent', color: 'var(--color-tint)', font: 'var(--type-subhead)', cursor: 'pointer',
          }}
        >
          Add a statement password
        </button>
      </Card>
      {adding && <PasswordSheet onClose={() => setAdding(false)} />}
    </div>
  );
}

function PasswordSheet({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => api.post('/statement-passwords', { label: label.trim(), password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onClose();
    },
    onError: (err) => {
      const e = err as ApiError;
      setFailure(
        e.status === 401
          ? 'Sign in again before saving a password — this needs a session less than ten minutes old.'
          : e.message,
      );
    },
  });

  return (
    <Sheet title="Add a statement password" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFailure(null);
          save.mutate();
        }}
      >
        <input
          style={fieldStyle}
          placeholder="What is it for? e.g. MF Central family CAS"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
          autoFocus
        />
        <input
          style={fieldStyle}
          placeholder="The password you set when requesting the statement"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="off"
          required
        />
        <p style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
          Encrypted before it is stored, and never shown again — it can be replaced but not read
          back. Add one for each password you use; every saved one is tried, most-successful first.
        </p>
        {failure && <p style={{ font: 'var(--type-subhead)', color: 'var(--color-status-critical)' }}>{failure}</p>}
        <PrimaryButton type="submit" disabled={save.isPending || !label.trim() || !password}>
          {save.isPending ? 'Saving…' : 'Save password'}
        </PrimaryButton>
      </form>
    </Sheet>
  );
}
